import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
} from "@learn-me/ui";
import {
  Check,
  X,
  GitCompare,
  ArrowRight,
  Loader2,
  Sparkles,
} from "lucide-react";

interface ContentDiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  original: { title: string; content: string };
  regenerated: { title: string; content: string };
  differences: string[];
  onApply: () => void;
  onDiscard: () => void;
  isApplying: boolean;
  sectionTitle: string;
}

export function ContentDiffModal({
  isOpen,
  onClose,
  original,
  regenerated,
  differences,
  onApply,
  onDiscard,
  isApplying,
  sectionTitle,
}: ContentDiffModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-purple-600" />
            Comparar cambios: {sectionTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Differences summary */}
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <h4 className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-1">
              <Sparkles className="h-4 w-4" />
              Cambios realizados:
            </h4>
            <ul className="text-sm text-purple-700 space-y-1">
              {differences.map((diff, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <ArrowRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{diff}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Side by side comparison */}
          <div className="flex-1 grid grid-cols-2 gap-4 overflow-hidden">
            {/* Original */}
            <div className="flex flex-col overflow-hidden border rounded-lg">
              <div className="px-3 py-2 bg-red-50 border-b border-red-200">
                <h4 className="text-sm font-medium text-red-800 flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Original
                </h4>
                {original.title !== regenerated.title && (
                  <p className="text-xs text-red-600 mt-1">
                    Título: {original.title}
                  </p>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 bg-red-50/30">
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: original.content }}
                />
              </div>
            </div>

            {/* Regenerated */}
            <div className="flex flex-col overflow-hidden border rounded-lg">
              <div className="px-3 py-2 bg-green-50 border-b border-green-200">
                <h4 className="text-sm font-medium text-green-800 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Propuesto
                </h4>
                {original.title !== regenerated.title && (
                  <p className="text-xs text-green-600 mt-1">
                    Título: {regenerated.title}
                  </p>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 bg-green-50/30">
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: regenerated.content }}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2 mt-4">
          <Button
            variant="outline"
            onClick={onDiscard}
            disabled={isApplying}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-2" />
            Descartar
          </Button>
          <Button
            onClick={onApply}
            disabled={isApplying}
            className="bg-green-600 hover:bg-green-700"
          >
            {isApplying ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Aplicar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
